"use client"

import clsx from "clsx"
import { useEffect, useState } from "react"
import { extractAndRecordCouponErrorMessage } from "src/checkout/hooks/extractCouponErrorMessage"
import { addCouponOrThrow, addItemToCart, removeItemFromCart, useCartState } from "src/checkout/hooks/useCart"
import { NpButton } from "src/components/np-design/components/button/NpButton"
import { npPagePtClassName, npPagePxClassName } from "src/components/np-design/components/constants"
import { NpErrorMessage } from "src/components/np-design/components/NpErrorMessage"
import { NpIcon } from "src/components/np-design/components/NpIcon"
import { setCartSidebarVisible } from "src/components/np-design/sections/cart-sidebar/state"
import { action_getAddToCartFromUrlDetails } from "src/components/sale/addToCartFromUrl/actions"
import { HeroText } from "src/components/styled/HeroText"
import { LoadingIndicator } from "src/components/styled/LoadingIndicator"
import { Img } from "src/components/unstyled/Img"
import { ResolvedListProductData } from "src/data/product-list-resolved/types"
import { SanitySectionProps } from "src/data/sanity/types"
import { useEventCallback } from "src/hooks/useEventCallback"
import { useIsClient } from "src/hooks/useIsClient"
import { genericErrorMessage } from "src/utilities/errorMessages"
import { recordError } from "src/utilities/errorReporting"
import { mapLineBreaksToHtmlBreaks } from "src/utilities/html"
import { ProductImageSize, toProductImageCdnUrl } from "src/utilities/productImages"

export type AddToCartFromUrlSectionProps = SanitySectionProps<"addToCartFromUrl">

export const AddToCartFromUrlSection = ({ heading, subtitle, description }: AddToCartFromUrlSectionProps) => {
  const { data: cart, loading: cartLoading } = useCartState()

  const [productLoading, setProductLoading] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)

  const [product, setProduct] = useState<ResolvedListProductData | null>(null)

  const [submitting, setSubmitting] = useState(false)
  const [submitFailed, setSubmitFailed] = useState(false)
  const [submitError, setSubmitError] = useState(genericErrorMessage)

  const [searchParams, setSearchParams] = useState(new URLSearchParams())
  const isClient = useIsClient()

  const code = searchParams.get("code")
  const itemIsInCart = !!product && !!cart?.items.some((item) => item.product.sku === product.sku)
  const codeIsActive = !!code && !!cart?.coupons.some((coupon) => coupon.code.toLowerCase() === code.toLowerCase())

  const load = useEventCallback(async () => {
    try {
      const searchParams = new URLSearchParams(window.location.search)
      setSearchParams(searchParams)

      const sku = searchParams.get("sku")
      const code = searchParams.get("code")

      if (!sku || !code) {
        setLoadFailed(true)
        return
      }

      setProductLoading(true)
      setLoadFailed(false)

      const { product } = await action_getAddToCartFromUrlDetails(sku, code)

      setProduct(product)
    } catch (error) {
      recordError("Error loading AddToCartFromUrl: " + searchParams, error)
      setLoadFailed(true)
    } finally {
      setProductLoading(false)
    }
  })

  useEffect(() => {
    void load()
  }, [load])

  const submit = useEventCallback(async () => {
    if (!product || !code) {
      throw new Error("Missing product or code")
    }

    let addedItemKey: string | undefined

    try {
      setSubmitting(true)
      setSubmitFailed(false)

      if (!itemIsInCart) {
        const newCart = await addItemToCart(product.id)
        addedItemKey = newCart.items.find((item) => item.product.sku === product.sku)?.key
        setCartSidebarVisible(false)
      }

      if (!codeIsActive) {
        await addCouponOrThrow(code)
        setCartSidebarVisible(false)
      }

      setCartSidebarVisible(true)
    } catch (error) {
      const message = extractAndRecordCouponErrorMessage(code, error)

      setSubmitFailed(true)
      setSubmitError(message)

      // rollback
      if (addedItemKey) {
        removeItemFromCart(addedItemKey)
      }
    } finally {
      setSubmitting(false)
    }
  })

  if (!isClient || cartLoading || productLoading) {
    return <LoadingIndicator />
  }

  if (loadFailed) {
    return <HeroText heading="Ungültige URL" description={genericErrorMessage} />
  }

  return (
    <section aria-label={heading || undefined} className={clsx(npPagePtClassName, npPagePxClassName)}>
      <div className="relative mx-auto flex w-240 max-w-full flex-col items-center gap-8 overflow-hidden text-center font-light text-pretty xl:gap-10">
        {heading && <h2 className="text-h2-np xl:text-h1-np m-0">{heading}</h2>}
        {subtitle && (
          <p
            className="text-lg xl:text-2xl"
            dangerouslySetInnerHTML={{ __html: mapLineBreaksToHtmlBreaks(subtitle || "") }}
          />
        )}

        {product && (
          <div className="flex w-101 max-w-full flex-1 flex-col items-center gap-6">
            <div className="flex aspect-square w-100 max-w-full overflow-hidden rounded-3xl">
              <Img
                src={toProductImageCdnUrl(product.image, ProductImageSize.Large)}
                alt=""
                className="aspect-square size-full object-cover"
              />
            </div>

            <div className="flex flex-col items-center gap-4 px-4 text-center leading-normal">
              <h3 className="font-product m-0 text-lg">{product.name}</h3>
              {product.slogan && <p className="text-lg text-pretty">{product.slogan}</p>}
            </div>
          </div>
        )}

        {itemIsInCart && codeIsActive ? (
          <div className="text-np-green flex flex-col items-center text-center">
            <NpIcon name="check_circle" className="size-7" />
            <div className="font-medium">Hinzugefügt</div>
          </div>
        ) : (
          <div className="mx-auto flex w-100 max-w-full grid-cols-1 flex-col items-stretch gap-4 whitespace-nowrap xl:gap-6">
            <NpButton size="md" loading={submitting} disabled={!isClient} onClick={submit}>
              Jetzt gratis hinzufügen
            </NpButton>

            {submitFailed && (
              <div className="text-base">
                <NpErrorMessage message={submitError} />
              </div>
            )}
          </div>
        )}

        {description && (
          <p
            className="text-left text-base xl:text-lg"
            dangerouslySetInnerHTML={{ __html: mapLineBreaksToHtmlBreaks(description || "") }}
          />
        )}
      </div>
    </section>
  )
}
